<template>
    <v-layout row>
        <v-flex xs12 sm12>
            <v-card class="elavation-1 transparent">
                <v-toolbar class="transparent" flat height="37">
                    <v-btn dark icon title="Back to menu" @click="backToBillingList">
                        <v-icon>arrow_back</v-icon>
                    </v-btn>
                    <v-spacer></v-spacer>
                    <!-- allow SOA preview if there is billing contact -->
                    <v-btn v-if="invoice&&hasBillingContact" icon dark :loading="loading_preview" @click="previewSoa" title="Preview SOA in PDF">
                        <v-icon>fas fa-file-pdf</v-icon>
                    </v-btn>
                    <v-menu v-if="!loading" left v-model="menu_status" offset-y :disabled="isPaid||!hasPayment||isPaymentForReview">
                        <template v-slot:activator="{ on }">
                            <v-btn :color="commonfn.getStatusButtonColor(invoice.status)" v-on="on" dark :loading="loading_menu">
                                &nbsp;&nbsp;{{commonfn.getStatusDescription(invoice.status)}}&nbsp;&nbsp;
                                <v-icon v-show="!menu_status">arrow_drop_down</v-icon>
                                <v-icon v-show="menu_status">arrow_drop_up</v-icon>
                            </v-btn>
                        </template>
                        <v-list dense>
                            <v-list-tile @click="setPaymentForReview">
                                <v-list-tile-title small>Payment For Review</v-list-tile-title>
                            </v-list-tile>
                        </v-list>
                    </v-menu>
                </v-toolbar>
                <v-progress-linear v-if="loading" indeterminate color="primary" height="3" class="mt-0"></v-progress-linear>
            </v-card>

            <v-alert v-if="error" :value="true" color="error" icon="error" outline class="mt-8">
                {{ errorMessage }}
            </v-alert>
            <v-alert v-if="invoice&&!payment_attachment" :value="true" color="info" icon="info" outline class="mt-8">
                <b>Please review the Statement of Account and billing details below.</b><br>
                Please make payment on or before <b>{{invoice.dueDate | moment('MMM DD, YYYY')}}</b> using the instructions in the SOA sent to your email.
                You may also click the PDF icon to view the SOA.<br>
                Once done, upload your proof of payment in the <b>Proof of Payment Attachment</b> section of this page.
            </v-alert>
            <v-alert v-if="invoice&&payment_attachment&&isIssued" :value="true" color="info" icon="info" outline class="mt-8">
                <b>Your proof of payment has been saved.</b><br>
                Click the <b class="clickable" @click="previewProofOfPayment">proof of payment attachment link</b> to preview your upload.
                If everything looks correct, set the status to "Payment For Review" to notify the SweldoNow Billing Team.<br>
                Once verified, the Printed Invoice will be emailed to you and attached to this page.
            </v-alert>
            <v-alert v-if="invoice&&payment_attachment&&isPaymentForReview" :value="true" color="info" icon="info" outline class="mt-8">
                <b>Thank you for your payment.</b><br>
                The SweldoNow Billing Team will verify your submission.<br>
                Once verified, we will notify you via email and attach the scanned copy of the printed invoice from the BIR to this page.
            </v-alert>
            <v-alert v-if="invoice&&isPendingPayment" :value="true" color="warning" icon="warning" outline class="mt-8">
                <b>Uncredited Payment Identified</b><br>
                {{billing.billingStartDate | moment('MMMM DD')}} to {{billing.billingEndDate | moment('DD, YYYY')}} payment remains uncredited based on our annual reconciliation. Kindly validate and make payment if unpaid. Once paid, set the status to <strong>Payment For Review</strong>
            </v-alert>
            <v-alert v-if="invoice&&hasAdjustedAmount" :value="true" color="info" icon="info" outline class="mt-8">
                <b>Billing Protection</b><br>
                Your bill will never decrease when adding more users. This ensures predictable billing — if adding users increases your total, the higher amount will apply. If adding users would normally reduce your total due to bracket changes, your previous month’s bill will stay the same.
            </v-alert>

            <!-- Service Items -->
            <v-card v-if="invoice" class="elavation-1 section-card">
                <v-toolbar flat height="40px">
                    <b class="primary--text">SERVICE ITEMS</b>
                    <v-spacer></v-spacer>
                </v-toolbar>
                <v-data-table
                    :headers="isMobile ? [] : header_service_items"
                    :items="invoiceServices"
                    :loading="loading"
                    :hide-headers="isMobile"
                    hide-actions
                >
                    <template v-slot:items="props">

                        <!-- mobile view -->
                        <tr v-if="isMobile">
                            <td :colspan="header_service_items.length" class="pa-1">
                                <v-card class="elevation-1">
                                    <v-layout row wrap align-center class="card-layout-mobile">
                                        <v-flex xs12 class="text-xs-left mt-5 mb-2">
                                            <v-layout row>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Service Name</div>
                                                    <div>{{ props.item.serviceName }}</div>
                                                </v-flex>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Price Per User Per Month</div>
                                                    <div>₱{{ numformat.formatNumber(props.item.pricePerUserPerMonth) }}</div>
                                                </v-flex>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Price Per User Per Year</div>
                                                    <div>₱{{ numformat.formatNumber(props.item.pricePerUserPerYear) }}</div>
                                                </v-flex>
                                            </v-layout>
                                            <v-layout row>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Current Month Due</div>
                                                    <div>₱{{ numformat.formatNumber(props.item.amountDue) }}</div>
                                                </v-flex>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Adjusted Amount</div>
                                                    <div v-if="props.item.adjustedAmount">₱{{ numformat.formatNumber(props.item.adjustedAmount) }}</div>
                                                    <div v-else>N/A</div>
                                                </v-flex>
                                            </v-layout>
                                        </v-flex>
                                    </v-layout>
                                </v-card>
                            </td>
                        </tr>

                        <!-- desktop view -->
                        <tr v-else>
                            <td>{{ props.item.serviceName }}</td>
                            <td>₱{{ numformat.formatNumber(props.item.pricePerUserPerMonth) }}</td>
                            <td>₱{{ numformat.formatNumber(props.item.pricePerUserPerYear) }}</td>
                            <td>₱{{ numformat.formatNumber(props.item.amountDue) }}</td>
                            <td>
                                <span v-if="props.item.adjustedAmount">₱{{ numformat.formatNumber(props.item.adjustedAmount) }}</span>
                                <span v-else>N/A</span>
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-card>

            <!-- Statement Of Account -->
            <v-card v-if="invoice" class="elavation-1 section-card">
                <v-toolbar flat height="40px">
                    <!-- name this to Billing Statement while there is no BIR approval to issue digital invoice -->
                    <b class="primary--text">STATEMENT OF ACCOUNT</b>
                    <v-spacer></v-spacer>
                </v-toolbar>
                <v-card-text>
                    <v-layout row wrap>
                        <v-flex xs12 sm6>
                            <!-- Display invoice number once issuance of digital invoice has been approved -->
                            <v-layout row wrap :class="isMobile?'section-layout-mobile':'section-layout'">
                                <v-flex xs4 class="text-xs-left">
                                    SOA Number :
                                </v-flex>
                                <v-flex xs8 class="font-weight-bold text-xs-left">
                                    {{invoice.invoiceNumber}}
                                </v-flex>
                            </v-layout>
                            <v-layout row wrap :class="isMobile?'section-layout-mobile':'section-layout'">
                                <v-flex xs4 class="text-xs-left">
                                    Billing Start Date :
                                </v-flex>
                                <v-flex xs8 class="font-weight-bold text-xs-left">
                                    {{billing.billingStartDate | moment('MMMM DD, YYYY')}}
                                </v-flex>
                            </v-layout>
                            <v-layout row wrap :class="isMobile?'section-layout-mobile':'section-layout'">
                                <v-flex xs4 class="text-xs-left">
                                    Billing End Date :
                                </v-flex>
                                <v-flex xs8 class="font-weight-bold text-xs-left">
                                    {{billing.billingEndDate | moment('MMMM DD, YYYY')}}
                                </v-flex>
                            </v-layout>
                            <v-layout row wrap :class="isMobile?'section-layout-mobile':'section-layout'">
                                <v-flex xs4 class="text-xs-left">
                                    Due Date :
                                </v-flex>
                                <v-flex xs8 class="font-weight-bold text-xs-left">
                                    {{invoice.dueDate | moment('MMM DD, YYYY')}}
                                </v-flex>
                            </v-layout>
                        </v-flex>
                        <v-flex xs12 sm6>
                            <v-layout row wrap :class="isMobile?'section-layout-mobile':'section-layout'">
                                <v-flex xs4 class="text-xs-left">
                                    Total Amount Due :
                                </v-flex>
                                <v-flex xs8 class="font-weight-bold text-xs-left">
                                    ₱{{numformat.formatNumber(invoice.totalAmountDue)}}
                                </v-flex>
                            </v-layout>
                            <v-layout row wrap :class="isMobile?'section-layout-mobile':'section-layout'">
                                <v-flex xs4 class="text-xs-left">
                                    Total Processed :
                                </v-flex>
                                <v-flex xs8 class="font-weight-bold text-xs-left">
                                    {{totalProcessedUsers}} Employees as of {{billing.billingEndDate | moment('MMMM DD, YYYY')}}
                                </v-flex>
                            </v-layout>
                            <v-layout row wrap :class="isMobile?'section-layout-mobile':'section-layout'">
                                <v-flex xs4 class="text-xs-left">
                                    Fully Active Employees :
                                </v-flex>
                                <v-flex xs8 class="font-weight-bold text-xs-left">
                                    ₱{{numformat.formatNumber(activeTotalAmount)}} ({{ activeCount }} employees)
                                </v-flex>
                            </v-layout>
                            <v-layout row wrap :class="isMobile?'section-layout-mobile':'section-layout'">
                                <v-flex xs4 class="text-xs-left">
                                    Newly Hired Employees :
                                </v-flex>
                                <v-flex xs8 class="font-weight-bold text-xs-left">
                                    ₱{{numformat.formatNumber(newHireTotalAmount)}} ({{ newHireCount }} employees)
                                </v-flex>
                            </v-layout>
                            <v-layout row wrap :class="isMobile?'section-layout-mobile':'section-layout'">
                                <v-flex xs4 class="text-xs-left">
                                    Deactivated Employees :
                                </v-flex>
                                <v-flex xs8 class="font-weight-bold text-xs-left">
                                    ₱{{numformat.formatNumber(deactivatedTotalAmount)}} ({{ deactivatedCount }} employees as of {{billing.billingEndDate | moment('MMMM DD, YYYY')}})
                                </v-flex>
                            </v-layout>
                        </v-flex>
                    </v-layout>
                </v-card-text>
            </v-card>

            <!-- Statement Of Account Details -->
            <v-card v-if="invoice" class="elavation-1 section-card">
                <v-toolbar flat height="40px">
                    <b class="primary--text">STATEMENT OF ACCOUNT DETAILS</b>
                    <v-spacer></v-spacer>
                </v-toolbar>
                <v-data-table
                    :headers="isMobile ? [] : header_details"
                    :items="invoiceDetails"
                    :loading="loading"
                    :rows-per-page-items="rowsPerPageItems"
                    :pagination.sync="paginationSync"
                    :hide-headers="isMobile"
                >
                    <template v-slot:items="props">

                        <!-- mobile view -->
                        <tr v-if="isMobile">
                            <td :colspan="header_details.length" class="pa-1">
                                <v-card class="elevation-1">
                                    <v-toolbar flat height="42">
                                        <v-chip class="user-chip-container" small>
                                            <v-avatar>
                                                <v-img ref="avatar" :src="awsS3Helper.getAvatarUrl(props.item.user.userId, account.accountNumber)"></v-img>
                                            </v-avatar>
                                            {{props.item.user.firstName}} {{props.item.user.lastName}}
                                        </v-chip>
                                        <v-spacer></v-spacer>
                                    </v-toolbar>
                                    <v-layout row wrap align-center class="card-layout-mobile">
                                        <v-flex xs12 class="text-xs-left mt-5 mb-2">
                                            <v-layout row>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Type</div>
                                                    <div>{{ getChargeType(props.item.type) }}</div>
                                                </v-flex>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Months Charged</div>
                                                    <div>{{ numformat.formatNumber(props.item.monthsCharged) }}</div>
                                                </v-flex>
                                                <v-flex xs4>
                                                    <div class="caption grey--text text--darken-1">Amount</div>
                                                    <div>₱{{ numformat.formatNumber(props.item.amount) }}</div>
                                                </v-flex>
                                            </v-layout>
                                        </v-flex>
                                    </v-layout>
                                </v-card>
                            </td>
                        </tr>

                        <!-- desktop view -->
                        <tr v-else>
                            <td>
                                <v-chip class="user-chip-container" small>
                                    <v-avatar>
                                        <v-img ref="avatar" :src="awsS3Helper.getAvatarUrl(props.item.user.userId, account.accountNumber)"></v-img>
                                    </v-avatar>
                                    {{props.item.user.firstName}} {{props.item.user.lastName}}
                                </v-chip>
                            </td>
                            <td>{{ getChargeType(props.item.type) }}</td>
                            <td>{{ numformat.formatNumber(props.item.monthsCharged) }}</td>
                            <td>₱{{ numformat.formatNumber(props.item.amount) }}</td>
                        </tr>
                    </template>
                </v-data-table>
            </v-card>

            <!-- Payment -->
            <v-card v-if="invoice" class="elavation-1 section-card">
                <v-toolbar flat height="40px">
                    <b v-if="!payment_attachment" class="orange--text">PROOF OF PAYMENT ATTACHMENT</b>
                    <b v-else class="primary--text">PROOF OF PAYMENT ATTACHMENT</b>
                    <v-spacer></v-spacer>
                    <v-btn v-if="payment_attachment&&isIssued" @click="showDelete" :loading="loading_delete_attachment" icon title="Click to delete Printed Invoice Attachment">
                        <v-icon small>delete</v-icon>
                    </v-btn>
                </v-toolbar>
                <v-progress-linear v-if="loading_proofofpayment" indeterminate height="2" class="mt-0"></v-progress-linear>
                <v-card-text>
                    <v-layout v-if="!payment_attachment" row wrap>
                        <v-flex xs12 sm12>
                            <div class="text-xs-center">
                                <vue-dropzone ref="myVueDropzone" 
                                    class="form-upload"
                                    id="dropzone" 
                                    :options="dropzoneOptions"
                                    v-on:vdropzone-success="uploadSuccess"
                                    v-on:vdropzone-file-added="fileAdded"
                                    ></vue-dropzone>
                            </div>
                        </v-flex>
                    </v-layout>
                    <v-layout v-else row wrap>
                        <v-flex xs12 sm8>
                            <v-layout row wrap :class="isMobile?'section-layout-mobile':'section-layout'">
                                <v-flex xs4 class="text-xs-left">
                                    File Attachment:
                                </v-flex>
                                <v-flex xs8 class="font-weight-bold text-xs-left">
                                    <a href="#" @click="previewProofOfPayment" title="Click to preview">
                                        {{ payment_attachment.fileName }}
                                    </a>
                                </v-flex>
                            </v-layout>
                        </v-flex>
                        <v-flex xs12 sm4>
                            <v-layout row wrap :class="isMobile?'section-layout-mobile':'section-layout'">
                                <v-flex xs4 class="text-xs-left">
                                    Uploaded On:
                                </v-flex>
                                <v-flex xs8 class="font-weight-bold text-xs-left">
                                    {{ payment_attachment.createdOn | moment('MMMM DD, YYYY') }}
                                </v-flex>
                            </v-layout>
                        </v-flex>
                    </v-layout>
                </v-card-text>
            </v-card>

            <!-- scanned copy of invoice, this serves as the official receipt -->
            <!-- future updates - digital/system generated invoice - must be approved by BIR -->
            <v-card v-if="printed_invoice_attachment" class="elavation-1 section-card">
                <v-toolbar flat height="40px">
                    <b class="primary--text">Scanned Printed Invoice</b>
                    <v-spacer></v-spacer>
                </v-toolbar>
                <v-progress-linear v-if="loading_uploading" indeterminate height="2" style="margin-top:-3px;"></v-progress-linear>
                <v-card-text>
                    <v-layout row wrap>
                        <v-flex xs12 sm8>
                            <v-layout row wrap :class="isMobile?'section-layout-mobile':'section-layout'">
                                <v-flex xs4 class="text-xs-left">
                                    Printed Invoice:
                                </v-flex>
                                <v-flex xs8 class="font-weight-bold text-xs-left">
                                    <a href="#" @click="previewPrintedInvoice" title="Click to preview">
                                        {{ printed_invoice_attachment.fileName }}
                                    </a>
                                </v-flex>
                            </v-layout>
                        </v-flex>
                        <v-flex xs12 sm4>
                            <v-layout row wrap :class="isMobile?'section-layout-mobile':'section-layout'">
                                <v-flex xs4 class="text-xs-left">
                                    Uploaded On:
                                </v-flex>
                                <v-flex xs8 class="font-weight-bold text-xs-left">
                                    {{ printed_invoice_attachment.createdOn | moment('MMMM DD, YYYY') }}
                                </v-flex>
                            </v-layout>
                        </v-flex>
                    </v-layout>
                </v-card-text>
            </v-card>

            <!-- Comments and Updates -->
            <!-- Note - this communication channel and update history will include users from customer company and SweldoNow OPC users
                Avatars from SweldoNow Users are exptected to be not visible.
                Todo - add fallback image if not exist 
            -->
            <v-layout row wrap v-if="pageQueryId">
                <v-flex xs12 sm6>
                    <comments-history v-if="!loading" :comments_data="commentsData"></comments-history>
                </v-flex>
                <v-flex xs12 sm6>
                    <update-history v-if="!loading" ref="updateHistoryRef" :page_query_id="pageQueryId"></update-history>
                </v-flex>
            </v-layout>
        </v-flex>

        <v-dialog v-model="dialogPreview" fullscreen hide-overlay transition="dialog-bottom-transition">
            <preview ref="refPdfPreviewComponet" :dialogPreview.sync="dialogPreview"></preview>
        </v-dialog>
        <v-dialog v-model="dialogOkCancel" persistent width="600">
            <ok-cancel :msg="dialogOkCancelMsg" alignLeft="true" :dialogOkCancel.sync="dialogOkCancel" @ok="onOkCancel"></ok-cancel>
        </v-dialog>

        <!-- status messaging component -->
        <status-messaging :page_query_id="pageQueryId" @refresh="updateHandler"></status-messaging>
    </v-layout>
</template>

<script>

import axios from 'axios'
import vue2Dropzone from 'vue2-dropzone'
import 'vue2-dropzone/dist/vue2Dropzone.min.css'
import billingApi from '@/api/billing'
import commonfn from '@/common/functions'
import numformat from '@/common/num_format'
import awsS3Helper from '@/helpers/aws-s3'
import AWSS3DocumentType from '@/constants/aws-s3-document-types'
import userPrefs from '@/helpers/user-prefs'
import rowsPerPageItems from '@/constants/rows-per-page'

export default {
    name: 'BillingSummary',
    metaInfo: {
        title: 'Billing Summary',
        meta: [
            { charset: 'utf-8' },
            { name: 'description', content: 'Billing Summary' },
            { name: 'viewport', content: 'width=device-width, initial-scale=1' }
            ]
    },
    components: {
        vueDropzone: vue2Dropzone,
        'preview': () => import('@/components/FilePreview/Preview'),
        'ok-cancel': ()=> import("@/components/BasicPopups/OkCancel"),
        'comments-history': () => import('@/components/Common/CommentsHistory'),
        'status-messaging': () => import('@/components/Common/StatusMessaging'),
        'update-history': () => import('@/components/Common/UpdatesHistory'),
    },
    data () {
        const routeName = this.$route.name
        const savedSort = userPrefs.getSort(routeName)
        
        return {
            rowsPerPageItems,
            commonfn,
            numformat,
            awsS3Helper,
            paginationSync: {
                rowsPerPage: userPrefs.getRowsPerPage(routeName),
                sortBy: savedSort.sortBy,
                descending: savedSort.descending,
            },
            error: false,
            errorMessage: '',
            loading: false,
            menu_status: false,
            loading_preview: false,
            loading_uploading: false,
            loading_delete_attachment: false,
            loading_proofofpayment: false,
            loading_menu: false,
            dialogPreview: false,
            dialogOkCancel: false,
            dialogOkCancelMsg: null,
            okCancelFunction: null,
            account: null,
            billing: [],
            header_service_items: [
                {text: 'Service Name',align: 'left',value: '', width: '15%', sortable:false},
                {text: 'Price Per User Per Month',align: 'left',value: '',  width: '15%', sortable:false},
                {text: 'Price Per User Per Year',align: 'left',value: '',  width: '15%', sortable:false},
                {text: 'Current Month Due',align: 'left',value: '',  width: '15%', sortable:false},
                {text: 'Adjusted Amount',align: 'left',value: '',  width: '15%', sortable:false},
            ],
            header_details: [
                {text: 'Employee',align: 'left',value: 'lastName', width: '20%', sortable:true},
                {text: 'Type',align: 'left',value: 'type', width: '20%', sortable:true},
                {text: 'Months Charged',align: 'left',value: 'monthsCharged', width: '20%', sortable:true},
                {text: 'Amount',align: 'left',value: 'amount', width: '20%', sortable:true},
            ],
            dropzoneOptions: {                    
                url: `${axios.defaults.baseURL}Billing/payment/attachment/upload`,    
                headers: { 
                    "Authorization": `Bearer ${localStorage.token}`,
                    "session_id": localStorage.sessionId,
                    "source": "Customer"
                },
                params: () => {
                    return {
                        invoiceId: this.selectedInvoiceId,
                        invoiceNumber: this.invoice.invoiceNumber,
                        companyId: this.companyId,
                        billingStartDate: this.billing.billingStartDate,
                        billingEndDate: this.billing.billingEndDate
                    }
                },
                addRemoveLinks: false,
                dictDefaultMessage: "<b class='subheading'><u>Drag and drop your file or click to browse</u><b><br><br><p class='caption'>Maximum 5MB per file.<br>Accepted files types: <br> Images (.jpg, .jpeg, .png) and PDF (.pdf)</p>",
                maxFiles: 1,
                acceptedFiles: [
                    'image/jpeg',
                    'image/jpg',
                    'image/png',
                    'application/pdf',
                    ].join(','),
                maxFilesize: 5,   // 5MB
            },
            serviceTypes: [],
            invoice: null,
            invoiceServices: [],
            invoiceDetails: [],
            billing_contacts: [],
            payment_attachment: null,
            printed_invoice_attachment: null,
            selectedInvoiceId: null
        }
    },
    methods: {
        onOkCancel() {
            if (typeof this.okCancelFunction === 'function') {
                this.okCancelFunction()
            }
            this.okCancelFunction = null
            this.dialogOkCancel = false
        },
        showDelete() {
            this.dialogOkCancelMsg = `This action will delete the Proof of Payment Attachment.<br> Do you want to proceed?`
            this.dialogOkCancel = true
            this.okCancelFunction = () => this.deleteProofOfPaymentAttachment() //can pass any method here
        },
        showProofOfPaymentUploadSuccess() {
            this.dialogOkCancelMsg = `<b>Your proof of payment has been saved.</b><br>Click <b>OK</b> to set the status to "Payment For Review" and notify the SweldoNow Billing Team.<br>Click <b>CANCEL</b> to review or replace your attachment.`
            this.dialogOkCancel = true
            this.okCancelFunction = () => this.setPaymentForReview() //can pass any method here
        },
        async setPaymentForReview() {
            try {
                this.loading_menu = true
                const data = {
                    companyId: this.companyId,
                    invoiceId: this.selectedInvoiceId
                }
                const response = await billingApi.forPaymentReview(data)
                if (response && response.status == 200) {
                    await this.getBillingDetails(false)

                    var companyAccount = this.$store.state.companyAccount
                    companyAccount.isPaymentOverdue = false
                    this.$store.commit('setCompanyAccount', companyAccount)
                }
                this.loading_menu = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        async deleteProofOfPaymentAttachment() {
            try {
                this.loading_delete_attachment = true
                const data = {
                    companyId: this.companyId,
                    invoiceId: this.selectedInvoiceId
                }
                const response = await billingApi.deleteProofOfPaymentAttachment(data)
                if (response && response.status == 200) {
                    await this.getBillingDetails(false)
                }
                this.loading_delete_attachment = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        backToBillingList() {
            this.$router.push(`/company/billing`)
        },
        uploadSuccess(file, response) {
            this.$refs.myVueDropzone.removeAllFiles(true)
            this.getBillingDetails(false)
            this.loading_uploading = false
            this.showProofOfPaymentUploadSuccess()
        },
        fileAdded(file) {
            this.loading_uploading = true
        },
        async previewProofOfPayment() {
            try {
                this.loading_proofofpayment = true
                const data = {
                    companyId: this.companyId,
                    invoiceId: this.selectedInvoiceId
                }
                const response = await billingApi.getPaymentAttachmentUrl(data)
                if (response && response.status == 200) {
                    var file = {
                        name: response.data.fileName,
                        type: 'Pdf',
                        url: response.data.url,
                        downloadUrl: awsS3Helper.getBillingDocumentUrl(this.account.accountNumber, response.data.fileName, AWSS3DocumentType.ProofOfPayment)
                    }
                    this.$refs.refPdfPreviewComponet.setParams(file)
                    this.dialogPreview = true
                }
                this.loading_proofofpayment = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        async previewPrintedInvoice() {
            try {
                this.loading_uploading = true
                const data = {
                    companyId: this.companyId,
                    invoiceId: this.selectedInvoiceId
                }
                const response = await billingApi.getPrintedInvoiceUrl(data)
                if (response && response.status == 200) {
                    var file = {
                        name: response.data.fileName,
                        type: 'Pdf',
                        url: response.data.url,
                        downloadUrl: awsS3Helper.getBillingDocumentUrl(this.account.accountNumber, response.data.fileName, AWSS3DocumentType.PrintedInvoice)
                    }
                    this.$refs.refPdfPreviewComponet.setParams(file)
                    this.dialogPreview = true
                }
                this.loading_uploading = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        async previewSoa() {
            try {
                this.loading_preview = true
                const response = await billingApi.getSoaUrl(this.companyId, this.selectedInvoiceId)
                if (response && response.status == 200) {
                    var file = {
                        name: response.data.fileName,
                        type: 'Pdf',
                        url: response.data.url,
                        downloadUrl: awsS3Helper.getBillingDocumentUrl(this.account.accountNumber, response.data.fileName, AWSS3DocumentType.SOA)
                    }
                    this.$refs.refPdfPreviewComponet.setParams(file)
                    this.loading_preview = false
                    this.dialogPreview = true
                }
            }catch(error){
                this.error = true
                this.errorMessage = error
                this.loading_preview = false
            }
        },
        async getBillingDetails(show_loading) {
            try {
                this.loading = show_loading
                const response = await billingApi.getBillingDetailsByInvoiceId(this.companyId, this.selectedInvoiceId)
                if (response && response.status == 200) {
                    this.account = response.data.account
                    this.billing = response.data.billing
                    this.invoice = response.data.invoice
                    this.invoiceServices = response.data.services
                    this.invoiceDetails = response.data.details
                    this.billing_contacts = response.data.contacts
                    this.printed_invoice_attachment = response.data.printedInvoiceAttachment
                    this.payment_attachment = response.data.invoicePaymentAttachment
                    
                    // Future Updates
                    // > Automate invoice pdf creation then request review/approval from BIR
                }
                this.loading = false
            }catch(error){
                this.error = true
                this.errorMessage = error
            }
        },
        getChargeType(type) {
            switch(type) {
                case 1:
                    return 'Fully Active'
                case 2:
                    return 'Deactivated'
                case 3:
                    return 'New Hire'
            }
        },
        updateHandler(data) {
            //exclude the the device/user who initiates the update
            if (data!=this.$store.state.userId) this.getBillingDetails(false)

            //invoke updateHistoryRef appendData 
            this.$refs.updateHistoryRef.getHistory()
        }
    },
    async created() {
        this.selectedInvoiceId = this.$route.params.invoiceId

        //update header titles
        this.$root.$emit('setHeaderTitles', {
            display: true,
            headerTitle: '',
            headerPageTitle: `Billing Details`
        })
        this.getBillingDetails(true)
    },
    watch: {
        'paginationSync.rowsPerPage'(newVal) {
            userPrefs.updateRowsPerPage(this.$route.name, newVal)
        },
        'paginationSync.sortBy'(newVal) {
            userPrefs.updateSort(this.$route.name, newVal, this.paginationSync.descending)
        },
        'paginationSync.descending'(newVal) {
            userPrefs.updateSort(this.$route.name, this.paginationSync.sortBy, newVal)
        }
    },
    computed: {
        companyId() {
            return this.$store.state.companyId
        },
        isMobile() {
            return window.innerWidth < 600
        },
        isDraft() {
            if (this.invoice) {
                return commonfn.getStatusDescription(this.invoice.status) == 'Draft'
            }
        },
        isIssued() {
            if (this.invoice) {
                return commonfn.getStatusDescription(this.invoice.status) == 'Issued'
            }
        },
        isPaid() {
            if (this.invoice) {
                return commonfn.getStatusDescription(this.invoice.status) == 'Paid'
            }
        },
        isPaymentForReview() {
            if (this.invoice) {
                return commonfn.getStatusDescription(this.invoice.status) == 'Payment For Review'
            }
        },
        isPendingPayment() {
            if (this.invoice) {
                return commonfn.getStatusDescription(this.invoice.status) == 'Pending Payment'
            }
        },
        totalProcessedUsers() {
            return this.invoiceDetails.length 
        },
        activeCount() {
            return this.invoiceDetails.filter(x => x.type == 1).length
        },
        activeTotalAmount() {
            return this.invoiceDetails
                .filter(x => x.type == 1)
                .map(x => x.amount)
                .reduce((prev,next)=> prev + next)
        },
        newHireCount() {
            return this.invoiceDetails.filter(x => x.type == 3).length
        },
        newHireTotalAmount() {
            var newHire = this.invoiceDetails.filter(x => x.type == 3)
            if (newHire && newHire.length) {
                return newHire
                    .map(x => x.amount)
                    .reduce((prev,next) => prev + next)
            }
            return 0
        },
        deactivatedCount() {
            return this.invoiceDetails.filter(x => x.type == 2).length
        },
        deactivatedTotalAmount() {
            var deactivated = this.invoiceDetails.filter(x => x.type == 2)
            if (deactivated && deactivated.length) {
                return deactivated
                        .map(x => x.amount)
                        .reduce((prev,next) => prev + next)
            }
            return 0
        },
        hasPrintedInvoiceAttached() {
            return this.printed_invoice_attachment
        },
        pageQueryId() {
            return this.selectedInvoiceId
        },
        commentsData() {
            return {
                pageQueryId: this.pageQueryId,
                accountNumber: this.account.accountNumber
            }
        },
        hasBillingContact() {
            return this.billing_contacts.length
        },
        hasPayment() {
            return this.payment_attachment
        },
        hasAdjustedAmount() {
            return this.invoiceServices.filter(x => x.adjustedAmount > 0).length > 0
        }
    }
}
</script>
<style scoped>
.section-layout {
    padding:0px 20px 0px 50px;
}
.section-layout-mobile {
    padding:0px 10px 0px 10px;
}
.section-card {
    margin-top:5px;
}
.form-upload {
    height:160px;
    margin:10px 0px 10px 0px;
    padding:0px 50px 0px 50px;
    border: 1px dashed darkgray;
    color: orange;
}
.clickable {
    cursor: pointer;
    text-decoration: underline;
}
.card-layout-mobile {
    padding:0px 23px 0px 23px;
}
</style>